/**
 * Shared Subscription Actions Module
 *
 * Provides reusable subscription management functionality (cancel, change plan, update payment)
 * that can be used across different pages (subscriptions tab, esim details, etc.)
 *
 */

(function () {
  "use strict";

  // Prevent multiple initializations
  if (window.SubscriptionActions && window.SubscriptionActions._eventDelegationSetup) {
    return;
  }

  // If module exists but event delegation not set up, set it up now
  if (window.SubscriptionActions && !window.SubscriptionActions._eventDelegationSetup) {
    window.SubscriptionActions.setupEventDelegation();
    return;
  }

  /**
   * Subscription Actions API
   * Exposed globally for use across pages
   */
  window.SubscriptionActions = {
    _initialized: false,
    _eventDelegationSetup: false,

    /**
     * Initialize the subscription actions system
     * @param {Object} config - Configuration object
     * @param {string} config.proxyBase - API proxy base URL
     * @param {string} config.customerId - Customer ID
     * @param {string} config.locale - Locale string (e.g., "en-GB")
     * @param {string} config.currency - Currency code (e.g., "GBP")
     * @param {Object} config.translations - Translation object
     * @param {Function} config.getCurrentContract - Function to get current contract
     * @param {Function} config.onContractUpdated - Callback when contract is updated
     * @param {Function} config.onError - Error callback
     */
    init(config = {}) {
      this.config = {
        proxyBase: config.proxyBase || "/apps/proxy",
        customerId: config.customerId || "",
        locale: config.locale || "en-GB",
        currency: config.currency || "GBP",
        translations: config.translations || {},
        getCurrentContract: config.getCurrentContract || (() => null),
        onContractUpdated: config.onContractUpdated || (() => {}),
        onError: config.onError || ((msg) => {}),
      };

      this._initialized = true;

      // Setup global event delegation for data-action buttons (if not already set up)
      this.setupEventDelegation();
    },

    /**
     * Setup event delegation for [data-action] buttons
     * Works globally across all pages
     * Can be called before init() - handlers will check initialization status
     */
    setupEventDelegation() {
      // Only setup once
      if (this._eventDelegationSetup) {
        return;
      }
      this._eventDelegationSetup = true;

      document.addEventListener("click", async (e) => {
        const btn = e.target.closest("[data-action]");
        if (!btn) return;

        const action = btn.getAttribute("data-action");
        if (!action) return;

        // Prevent default if it's a button
        if (btn.tagName === "BUTTON" || btn.closest("button")) {
          e.preventDefault();
        }

        // Check if module is initialized
        if (!this._initialized || !this.config || !this.config.proxyBase) {
          alert("Subscription actions not initialized. Please wait for the page to finish loading and try again.");
          return;
        }

        try {
          switch (action) {
            case "cancel-plan":
              await this.handleCancelPlan();
              break;
            case "change-plan":
              await this.handleChangePlan();
              break;
            case "update-payment":
              await this.handleUpdatePayment();
              break;
          }
        } catch (error) {
          this.config.onError(error.message || "An error occurred");
        }
      });
    },

    /**
     * API helper function
     * @param {string} path - API endpoint path
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} API response
     */
    async api(path, params = {}) {
      const normalized = path.replace(/^\/+/, "");
      const url = new URL(`${this.config.proxyBase}/${normalized}`, window.location.origin);

      if (this.config.customerId) {
        url.searchParams.set("logged_in_customer_id", this.config.customerId);
      }

      Object.entries(params).forEach(([k, v]) => {
        if (v != null) url.searchParams.set(k, v);
      });

      try {
        const res = await fetch(url.toString(), { credentials: "include" });

        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`Request failed ${res.status}: ${errorText}`);
        }

        return await res.json();
      } catch (error) {
        throw error;
      }
    },

    /**
     * Translation helper
     * @param {string} key - Translation key
     * @param {string} fallback - Fallback text
     * @returns {string} Translated text
     */
    T(key, fallback) {
      return (this.config.translations && this.config.translations[key]) || fallback;
    },

    /**
     * Template helper for string interpolation
     * @param {string} str - Template string with {{key}} placeholders
     * @param {Object} vars - Variables object
     * @returns {string} Interpolated string
     */
    tpl(str, vars) {
      const s = typeof str === "string" ? str : "";
      return s.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, p) => (vars && p in vars ? String(vars[p]) : ""));
    },

    /**
     * Format date helper
     * @param {string|number|Object} isoOrTimestampSeconds - Date value
     * @returns {string} Formatted date string
     */
    formatDate(isoOrTimestampSeconds) {
      if (!isoOrTimestampSeconds) return "";

      let date;

      switch (typeof isoOrTimestampSeconds) {
        case "string":
          date = new Date(isoOrTimestampSeconds);
          break;
        case "number":
          date = new Date(isoOrTimestampSeconds * 1000);
          break;
        case "object":
          if (isoOrTimestampSeconds._seconds) {
            date = new Date(isoOrTimestampSeconds._seconds * 1000);
          }
          break;
      }

      if (!date || isNaN(date.getTime())) return "";

      return date.toLocaleDateString(this.config.locale, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    },

    /**
     * Show modal dialog
     * @param {string} title - Modal title
     * @param {string} bodyHTML - Modal body HTML
     * @param {Function} onConfirm - Confirm callback
     */
    showModal(title, bodyHTML, onConfirm) {
      const backdrop = document.getElementById("subs-modal-backdrop");
      const modal = document.getElementById("subs-modal");
      const titleEl = document.getElementById("subs-modal-title");
      const bodyEl = document.getElementById("subs-modal-body");
      const confirmBtn = document.getElementById("subs-modal-confirm");
      const cancelBtn = document.getElementById("subs-modal-cancel");
      const closeBtn = document.getElementById("subs-modal-close");

      if (!backdrop || !modal || !titleEl || !bodyEl) {
        console.error("Subscription modals not found. Ensure subscriptions-modals snippet is rendered.");
        return;
      }

      titleEl.textContent = title;
      bodyEl.innerHTML = bodyHTML;
      backdrop.classList.remove("hidden");
      backdrop.classList.add("flex");
      modal.classList.remove("hidden");

      const cleanup = () => {
        if (confirmBtn) {
          confirmBtn.disabled = false;
          confirmBtn.textContent = this.T("modalConfirm", "Confirm");
        }
        backdrop.classList.add("hidden");
        backdrop.classList.remove("flex");
        modal.classList.add("hidden");
        if (confirmBtn) confirmBtn.onclick = null;
        if (cancelBtn) cancelBtn.onclick = null;
        if (closeBtn) closeBtn.onclick = null;
      };

      if (cancelBtn) cancelBtn.onclick = cleanup;
      if (closeBtn) closeBtn.onclick = cleanup;
      if (confirmBtn) {
        confirmBtn.onclick = async () => {
          try {
            await onConfirm?.();
          } finally {
            cleanup();
          }
        };
      }
    },

    /**
     * Show toast notification
     * @param {string} message - Toast message
     */
    toastShow(message) {
      const wrap = document.getElementById("subs-toast");
      const body = document.getElementById("subs-toast-body");

      if (!wrap || !body) return;

      body.textContent = message || "";

      wrap.classList.remove("hidden");
      wrap.classList.add("show");

      setTimeout(() => {
        try {
          wrap.classList.remove("show");
          wrap.classList.add("hidden");
        } catch (_) {}
      }, 2400);
    },

    /**
     * Handle cancel plan action
     * @param {Object} [contractOverride] - Optional contract to use instead of getCurrentContract()
     */
    async handleCancelPlan(contractOverride = null) {
      const currentContract = contractOverride || (await Promise.resolve(this.config.getCurrentContract()));

      if (!currentContract?.id) {
        this.config.onError("No contract found. Please ensure you have an active subscription.");
        return;
      }

      const body = `
        <div class="grid gap-4">
          <p class="text-lg font-semibold leading-6">${this.T(
            "modalCancelBody",
            "Are you sure you want to cancel this subscription?"
          )}</p>
          <div class="text-base text-gray-600 leading-relaxed">${this.T(
            "modalCancelGuide",
            "Cancelling your plan will stop future payments for this subscription. Your plan will continue to function until the end of the current billing cycle; at this time, your plan will be revoked."
          )}</div>
        </div>`;

      this.showModal(this.T("modalCancelTitle", "Cancel Subscription"), body, async () => {
        const confirmBtn = document.getElementById("subs-modal-confirm");
        if (confirmBtn) {
          confirmBtn.disabled = true;
          confirmBtn.textContent = this.T("saving", "Saving…");
        }

        try {
          // Save contract ID before canceling
          const contractId = currentContract.id;
          const iccid = currentContract.iccid;

          // Cancel the contract
          await this.api("api/cancel-contract", { contractId, iccid });

          // Refresh contract data to get the updated contract
          const contracts = await this.api("api/list-contracts");
          const allContracts = contracts?.data?.contracts || contracts?.contracts || [];

          // Find the updated contract by iccid (it should now have status CANCELLED)
          const updatedContract = allContracts.find((c) => c?.iccid === iccid);

          // Notify listeners of contract update (handle async callback errors)
          try {
            await Promise.resolve(this.config.onContractUpdated(updatedContract));
          } catch (callbackError) {
            console.error("Error in onContractUpdated callback:", callbackError);
            // Don't fail the whole operation if callback has an error
          }

          this.toastShow(this.T("toastCancelled", "Subscription cancelled"));

          // Dispatch custom event for UI updates
          window.dispatchEvent(
            new CustomEvent("subscription:cancelled", {
              detail: { contract: updatedContract },
            })
          );
        } catch (e) {
          // Log the actual error for debugging
          console.error("Cancel subscription error:", e);
          this.config.onError(e.message || "Failed to cancel subscription");
          this.toastShow(this.T("toastError", "Something went wrong. Please try again."));
        }
      });
    },

    /**
     * Handle change plan action
     * @param {Object} [contractOverride] - Optional contract to use instead of getCurrentContract()
     */
    async handleChangePlan(contractOverride = null) {
      const currentContract = contractOverride || (await Promise.resolve(this.config.getCurrentContract()));

      if (!currentContract?.id) {
        this.config.onError("No contract found. Please ensure you have an active subscription.");
        return;
      }

      // Get available variants from global index (includes sellingPlans)
      const index = window.subsVariantIndex || {};
      const bundleCode = currentContract?.bundle || currentContract?.sku;
      const variants = (index[bundleCode] || []).map((v) => ({
        sku: v.sku,
        title: v.title,
        variantId: v.variantId,
        price: typeof v.priceMinor === "number" ? v.priceMinor / 100 : undefined,
        available: v.available,
        sellingPlans: (v.sellingPlans || []).map((sp) => ({
          id: sp.id,
          name: sp.name,
          price: typeof sp.priceMinor === "number" ? sp.priceMinor / 100 : undefined,
        })),
      }));

      const effective = currentContract?.contractRenewalDate || currentContract?.nextBillingAttemptDate || null;

      const header = `<div class="text-base mb-3">${this.T(
        "modalChangePlanBody",
        "Select a new plan for your subscription."
      )}</div>`;

      if (!variants.length) {
        this.showModal(
          this.T("modalChangePlanTitle", "Change Plan"),
          `${header}<div class="text-base text-gray-600">${this.T(
            "changePlanNoOptions",
            "No other plans are available for this subscription."
          )}</div>`,
          () => {}
        );
        return;
      }

      // Format currency
      const moneyMajor = (amount) => {
        if (amount === null || amount === undefined || amount === "") return "—";
        const num = typeof amount === "string" ? parseFloat(amount) : amount;
        try {
          return new Intl.NumberFormat(this.config.locale, {
            style: "currency",
            currency: this.config.currency,
            currencyDisplay: "narrowSymbol",
          }).format(num);
        } catch (_) {
          return `${num}`;
        }
      };

      // Build variant selection rows
      const rows = variants
        .map((v) => {
          // Use bundleCode for comparison (same value used to look up variants)
          const isCurrent = v?.sku === bundleCode;
          const badge = isCurrent
            ? `<span class="ml-2 text-sm px-2 py-1 rounded bg-[rgba(var(--color-base-accent-2),0.15)]">${this.T(
                "changePlanCurrentBadge",
                "Current"
              )}</span>`
            : "";
          // Use first selling plan price if available, otherwise variant price
          const displayPrice = v?.sellingPlans?.[0]?.price ?? v?.price;
          const price = displayPrice ? moneyMajor(displayPrice) : "";
          return `
            <label class="flex items-center justify-between gap-4 rounded-[10px] border px-4 py-3 cursor-pointer" data-variant-sku="${
              v.sku
            }">
              <div class="flex items-center gap-3 min-w-0">
                <input type="radio" name="subs-change-plan" class="!m-0" value="${v.variantId || v.sku}" 
                  data-sku="${v.sku}" ${isCurrent ? "checked" : ""} ${isCurrent ? "disabled" : ""} />
                <div class="text-base font-semibold truncate">${v.title || v.sku}${badge}</div>
              </div>
              <div class="text-sm opacity-70 shrink-0 subs-variant-price" data-sku="${v.sku}">${price} ${this.T(
            "changePlanPerCycle",
            "per cycle"
          )}</div>
            </label>`;
        })
        .join("");

      // Build selling plan dropdown (hidden by default, shown when variant has multiple plans)
      const sellingPlanDropdown = `
        <div id="subs-selling-plan-wrapper" class="hidden pt-3">
          <label class="block text-sm font-medium mb-2" for="subs-selling-plan-select">
            ${this.T("changePlanBillingDurationLabel", "Billing Duration")}
          </label>
          <select id="subs-selling-plan-select" 
            class="w-full border rounded-lg px-3 py-2 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-base-accent-2))]">
          </select>
        </div>`;

      const effectiveText = effective
        ? `<div class="text-sm text-gray-600">${this.tpl(
            this.T("changePlanEffectiveOn", "This change will take effect on {{date}}."),
            { date: this.formatDate(effective) }
          )}</div>`
        : "";

      const body = `${header}<div class="grid gap-3">${rows}</div>${sellingPlanDropdown}<div class="pt-3">${effectiveText}</div>`;

      this.showModal(this.T("modalChangePlanTitle", "Change Plan"), body, async () => {
        const selected = document.querySelector('input[name="subs-change-plan"]:checked');
        if (!selected || !currentContract?.id) return;

        const val = selected.value;
        const chosen = variants.find((v) => v.variantId === val || v.sku === val);
        const confirmBtn = document.getElementById("subs-modal-confirm");

        // Get selected selling plan ID (if dropdown is visible and has selection)
        const sellingPlanSelect = document.getElementById("subs-selling-plan-select");
        const sellingPlanWrapper = document.getElementById("subs-selling-plan-wrapper");
        let sellingPlanId = null;
        if (sellingPlanWrapper && !sellingPlanWrapper.classList.contains("hidden") && sellingPlanSelect?.value) {
          sellingPlanId = sellingPlanSelect.value;
        } else if (chosen?.sellingPlans?.length === 1) {
          // If only one selling plan, use it automatically
          sellingPlanId = chosen.sellingPlans[0].id;
        }

        if (confirmBtn) {
          confirmBtn.disabled = true;
          confirmBtn.textContent = this.T("saving", "Saving…");
        }

        try {
          const apiParams = {
            contractId: currentContract.id,
            variantId: chosen?.variantId,
            sku: chosen?.sku,
            iccid: currentContract.iccid,
          };

          // Include selling plan ID if available
          if (sellingPlanId) {
            apiParams.sellingPlanId = sellingPlanId;
          }

          await this.api("api/schedule-plan-change", apiParams);

          // Refresh contract data to get the updated nextBundle and nextBundlePrice
          const contracts = await this.api("api/list-contracts");
          const allContracts = contracts?.data?.contracts || contracts?.contracts || [];
          const updatedContract = allContracts.find((c) => c?.iccid === currentContract.iccid) || currentContract;

          // Notify listeners of contract update
          try {
            await Promise.resolve(this.config.onContractUpdated(updatedContract));
          } catch (callbackError) {
            console.error("Error in onContractUpdated callback:", callbackError);
          }

          this.toastShow(this.T("changePlanScheduledToast", "Plan change scheduled"));

          // Dispatch custom event for UI updates
          window.dispatchEvent(
            new CustomEvent("subscription:plan-changed", {
              detail: { contract: updatedContract, newVariant: chosen, sellingPlanId },
            })
          );
        } catch (e) {
          this.config.onError(e.message || "Failed to change plan");
          this.toastShow(this.T("toastError", "Something went wrong. Please try again."));
        }
      });

      // Setup event listener for variant selection changes to update selling plan dropdown
      this._setupSellingPlanDropdown(variants, moneyMajor);
    },

    /**
     * Setup selling plan dropdown behavior for change plan modal
     * @param {Array} variants - Array of variant objects with sellingPlans
     * @param {Function} moneyMajor - Currency formatting function
     */
    _setupSellingPlanDropdown(variants, moneyMajor) {
      const wrapper = document.getElementById("subs-selling-plan-wrapper");
      const select = document.getElementById("subs-selling-plan-select");
      const radios = document.querySelectorAll('input[name="subs-change-plan"]');

      if (!wrapper || !select || !radios.length) return;

      // Helper to update the price display on a variant row
      const updateVariantPrice = (sku, price) => {
        const priceEl = document.querySelector(`.subs-variant-price[data-sku="${sku}"]`);
        if (priceEl) {
          const priceText = price ? moneyMajor(price) : "";
          priceEl.textContent = `${priceText} ${this.T("changePlanPerCycle", "per cycle")}`;
        }
      };

      const updateDropdown = (variantId, sku) => {
        const variant = variants.find((v) => v.variantId === variantId || v.sku === sku);
        const plans = variant?.sellingPlans || [];

        // Only show dropdown if there are multiple selling plans
        if (plans.length > 1) {
          // Populate dropdown options
          select.innerHTML = plans
            .map((sp) => {
              const priceText = sp.price
                ? `${moneyMajor(sp.price)} ${this.T("changePlanBillingDurationPerCycle", "per cycle")}`
                : "";
              return `<option value="${sp.id}">${sp.name}${priceText ? ` — ${priceText}` : ""}</option>`;
            })
            .join("");

          wrapper.classList.remove("hidden");

          // Update the variant price to show first selling plan price
          if (plans[0]?.price) {
            updateVariantPrice(sku, plans[0].price);
          }
        } else {
          wrapper.classList.add("hidden");
          select.innerHTML = "";

          // If only one selling plan, update price to that plan's price
          if (plans.length === 1 && plans[0]?.price) {
            updateVariantPrice(sku, plans[0].price);
          }
        }
      };

      // Listen for selling plan dropdown changes to update the displayed price
      select.addEventListener("change", (e) => {
        const checkedRadio = document.querySelector('input[name="subs-change-plan"]:checked');
        if (!checkedRadio) return;

        const sku = checkedRadio.dataset.sku;
        const variant = variants.find((v) => v.sku === sku);
        const selectedPlan = variant?.sellingPlans?.find((sp) => sp.id === e.target.value);

        if (selectedPlan?.price) {
          updateVariantPrice(sku, selectedPlan.price);
        }
      });

      // Listen for variant selection changes
      radios.forEach((radio) => {
        radio.addEventListener("change", (e) => {
          if (e.target.checked) {
            updateDropdown(e.target.value, e.target.dataset.sku);
          }
        });
      });

      // Initialize dropdown for currently selected variant
      const checkedRadio = document.querySelector('input[name="subs-change-plan"]:checked');
      if (checkedRadio) {
        updateDropdown(checkedRadio.value, checkedRadio.dataset.sku);
      }
    },

    /**
     * Handle update payment action
     */
    async handleUpdatePayment() {
      const currentContract = await Promise.resolve(this.config.getCurrentContract());

      if (!currentContract?.id) {
        this.config.onError("No contract found. Please ensure you have an active subscription.");
        return;
      }

      const data = await this.api("api/list-payment-methods");
      const methods = data?.data?.paymentMethods || data?.paymentMethods || [];

      const methodRow = (m, i) => `
        <label class="flex items-center justify-between gap-4 rounded-[10px] border px-4 py-3 cursor-pointer">
          <div class="flex items-center gap-3 min-w-0">
            <input type="radio" name="subs-payment-method" class="!m-0" value="${m.id}" ${i === 0 ? "checked" : ""} />
            <span class="w-16 h-7 rounded bg-[rgba(var(--color-base-accent-2),0.12)] flex items-center justify-center text-[rgb(var(--color-base-accent-1))] text-sm font-semibold">${(
              m.brand || ""
            ).toUpperCase()}</span>
            <span class="text-base truncate">•••• ${m.last4}</span>
          </div>
          <div class="text-sm opacity-70 shrink-0">${this.T("expShort", "Exp")} ${String(m.expiryMonth).padStart(
        2,
        "0"
      )}/${String(m.expiryYear).slice(-2)}</div>
        </label>`;

      const radios = methods.map(methodRow).join("");

      const addNewPaymentMethod = `<a href="/account/addresses" target="_blank" class="text-base font-semibold text-[rgb(var(--color-base-accent-2))] hover:underline">${this.T(
        "modalAddNewPaymentMethod",
        "Add a new payment method"
      )}</a>`;

      const body = methods.length
        ? `<div class="grid gap-3">${radios}<div class="pt-3">${addNewPaymentMethod}</div></div>`
        : `<div class="text-base text-gray-600">${this.T(
            "modalNoPaymentMethodsFound",
            "No payment methods found."
          )}</div>`;

      this.showModal(this.T("modalUpdatePaymentTitle", "Update Payment Method"), body, async () => {
        const selected = document.querySelector('input[name="subs-payment-method"]:checked');
        if (!selected || !currentContract?.id) return;

        const confirmBtn = document.getElementById("subs-modal-confirm");
        if (confirmBtn) {
          confirmBtn.disabled = true;
          confirmBtn.textContent = this.T("saving", "Saving…");
        }

        try {
          await this.api("api/update-contract-payment-method", {
            contractId: currentContract.id,
            paymentMethodId: selected.value,
          });

          const fresh = await this.api("api/list-payment-methods");
          const list = fresh?.data?.paymentMethods || fresh?.paymentMethods || [];
          const mapped = list.find((pm) => pm.id === currentContract?.paymentMethodId) || list[0];

          // Notify listeners of contract update (handle async callback errors)
          try {
            await Promise.resolve(this.config.onContractUpdated(currentContract));
          } catch (callbackError) {
            console.error("Error in onContractUpdated callback:", callbackError);
            // Don't fail the whole operation if callback has an error
          }

          this.toastShow(this.T("toastPaymentUpdated", "Payment method updated"));

          // Dispatch custom event for UI updates
          window.dispatchEvent(
            new CustomEvent("subscription:payment-updated", {
              detail: { contract: currentContract, paymentMethod: mapped },
            })
          );
        } catch (e) {
          this.config.onError(e.message || "Failed to update payment method");
          this.toastShow(this.T("toastError", "Something went wrong. Please try again."));
        }
      });
    },

    /**
     * Setup event delegation immediately on module load
     * This ensures buttons work even if init() is called later
     */
    _setupEventDelegationImmediate() {
      if (this._eventDelegationSetup) return;
      this.setupEventDelegation();
    },
  };

  // Setup event delegation immediately when module loads
  // This ensures buttons work even if init() is called later (e.g., after async data loads)
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      window.SubscriptionActions._setupEventDelegationImmediate();
    });
  } else {
    // DOM already loaded, set up immediately
    window.SubscriptionActions._setupEventDelegationImmediate();
  }
})();
